import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { WaiverFormData } from 'src/models/user/_types';
import { submitBusinessWaiver } from 'src/models/user/user';
import api from 'src/utils/ajax';
import * as Yup from 'yup';

export const useBusinessWaiver = () => {
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
  const { user, userLoading } = useOptionalSession();
  const [brandValue, setBrandValue] = useState('');

  const SubmitForm = async (values: WaiverFormData, { setSubmitting }: any) => {
    try {
      // Add your API call here
      const formdata = { ...values, subdomain: subdomain?.data };
      const response = await submitBusinessWaiver(formdata);
      setIsSubmitSuccess(true);

      // Use the secure redirect endpoint that will redirect to the subdomain with the token
      window.open(
        `${window.location.origin}/api/v4/secure_redirect?token=${response.redirectToken}`,
        '_self'
      );
    } catch (error) {
      setSubmitting(false);
    }
  };

  const {
    data: subdomain,
    isLoading: subdomainLoading,
    error: subdomainError,
    refetch: refetchSubdomain,
  } = useQuery({
    queryKey: ['subdomain', brandValue],

    queryFn: () => {
      return api.get(`/api/v4/users/${user?.id}/new_platform_subdomain`, {
        params: { brand: brandValue },
      });
    },

    enabled: !!user,
  });

  const {
    data: skipBusinessWaiver,
    isLoading: skipBusinessWaiverLoading,
    error: skipBusinessWaiverError,
  } = useQuery({
    queryKey: ['buisnessWaiver'],

    queryFn: () => {
      return api.get(`/api/v4/users/skip_business_waiver`);
    },

    enabled: !!user,
  });

  // Update subdomain when brand changes
  useEffect(() => {
    if (brandValue && user) {
      refetchSubdomain();
    }
  }, [brandValue, user, refetchSubdomain]);

  let validationSchema = Yup.object().shape({
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    timeZone: Yup.string().required('Time zone is required'),
    streetAddress: Yup.string().required('Street address is required'),
    city: Yup.string().required('City is required'),
    state: Yup.string().required('State is required'),
    zipCode: Yup.string().required('Zip code is required'),
    brand: Yup.string().required('Brand name is required'),
    currency: Yup.string().required('Currency is required'),
    signature: Yup.string().required('Signature is required'),
    countryAddress: Yup.string().required('Country is required'),
  });

  if (
    !skipBusinessWaiverLoading &&
    (skipBusinessWaiver?.data?.skip === false || skipBusinessWaiverError)
  ) {
    validationSchema = validationSchema.concat(
      Yup.object().shape({
        acceptedWaiver: Yup.boolean()
          .oneOf([true], 'You must accept the waiver')
          .required('You must accept the waiver'),
      })
    );
  }

  if (user?.needsPasswordReset) {
    validationSchema = validationSchema.concat(
      Yup.object().shape({
        password: Yup.string().required('Password is required'),
        confirmPassword: Yup.string()
          .required('Confirm password is required')
          .oneOf([Yup.ref('password')], 'Passwords must match'),
      })
    );
  }

  const currencies = [
    { label: '$ USD', value: 'usd' },
    { label: '€ EUR', value: 'eur' },
    { label: '£ GBP', value: 'gbp' },
    { label: 'HK$ HKD', value: 'hkd' },
    { label: 'R$ BRL', value: 'brl' },
    { label: 'CA$ CAD', value: 'cad' },
    { label: 'NZ$ NZD', value: 'nzd' },
    { label: 'A$ AUD', value: 'aud' },
    { label: 'S$ SGD', value: 'sgd' },
    { label: 'kr SEK', value: 'sek' },
    { label: 'Fr CHF', value: 'chf' },
    { label: 'د.إ AED', value: 'aed' },
    { label: 'MX$ MXN', value: 'mxn' },
  ];

  return {
    user,
    userLoading,
    SubmitForm,
    validationSchema,
    currencies,
    subdomain,
    subdomainLoading,
    subdomainError,
    isSubmitSuccess,
    skipBusinessWaiver,
    skipBusinessWaiverLoading,
    skipBusinessWaiverError,
    setBrandValue,
  };
};
